// packages/web/src/lib/useRecordDetail.ts — ported 2026-07-29 from the Rexx
// fork's 2026-07-21 UI rework: every detail screen (Client, Location, Work
// Order, ...) fetches the same three things (a type's schema, one record,
// its links) and needs to re-fetch the moment anything in the tenant changes
// (bumpRecordsVersion — recordEditor.ts). One hook, so screens don't each
// reinvent the load/refresh dance.
import { useCallback, useEffect, useState } from 'preact/hooks';
import {
  getRecordTypeSchema,
  getRecordWithLinks,
  type LinkRow,
  type RecordRow,
  type RecordTypeSchema,
} from './api';
import { describeReadFailure, type ReadFailure } from './readFailure';
import { useRecordsVersion } from './recordEditor';

export interface RecordDetailState {
  schema: RecordTypeSchema | null;
  record: RecordRow | null;
  links: LinkRow[];
  /** The primary read's failure, in the product's own voice — "there is
   * none" and "we could not read it" must never render alike, and a retry
   * is only offered where retrying can work (never on a refusal). */
  error: ReadFailure | null;
  reload: () => void;
}

export function useRecordDetail(tenantId: string, typeKey: string, id: string): RecordDetailState {
  const [schema, setSchema] = useState<RecordTypeSchema | null>(null);
  const [record, setRecord] = useState<RecordRow | null>(null);
  const [links, setLinks] = useState<LinkRow[]>([]);
  const [error, setError] = useState<ReadFailure | null>(null);
  const recordsVersion = useRecordsVersion();
  const [nonce, setNonce] = useState(0);

  const load = useCallback(async () => {
    try {
      const [schemaResult, recordResult] = await Promise.all([
        getRecordTypeSchema(tenantId, typeKey),
        getRecordWithLinks(tenantId, id),
      ]);
      setSchema(schemaResult);
      setRecord(recordResult.record);
      setLinks(recordResult.links);
      setError(null);
    } catch (err) {
      setError(describeReadFailure(err));
    }
  }, [tenantId, typeKey, id]);

  useEffect(() => {
    void load();
  }, [load, recordsVersion, nonce]);

  return { schema, record, links, error, reload: () => setNonce((n) => n + 1) };
}
